import Vue from "vue";
import { migrateToOldFormat, migrationEnabled } from "./migration";
import { appendItem, clearParents } from "./relations-adapter";
import router from "./router";
import selection from "./selection";
import {
  CONTAINER_TYPES,
  NO_PARENT,
  MULTI,
  ORDRD,
  RELATION_CONFIG,
  REL_ELEM,
  REL_LABL,
  REL_PLAN,
  getRelationForType,
  supportedViews,
  typeIconMapping,
  ORDINARY_TYPES,
} from "./structure";
import {
  calcRepeatDate,
  formatDateItem,
  nowTimestamp,
  repeatingDoesntMakeSense,
} from "./time-utils";
import settings from "./synced-settings";
import { appendItems } from "./ordered-relation";
import hintsShown from "./hints";
import { downloadPlaintext } from "./utils";

// Legacy format from old version.
const prefixMap = {
  board: "board",
  task: "task",
  note: "task",
  goal: "task",
  list: "list",
  period: "list",
  label: "label",
};

// A thin wrapper for a doc, providing a prototype with some useful
// properties. Prototype properties are not copied with Object.assign,
// so creating new doc value with Object.assign({}, item, ...) is not
// affected by the wrapper at all.
export class Item {
  constructor(doc, cache, db) {
    Object.assign(this, doc);
    // TODO maybe don't make cache a property of Item but just access
    // global singleton? con: cache exposes some methods that should be internal.
    Object.defineProperty(this, "cache", { value: cache });
    Object.defineProperty(this, "db", { value: db });
  }
  getChildren() {
    const index = this.cache.getIndex(this.childRel, this._id);
    switch (RELATION_CONFIG[this.childRel]) {
      case ORDRD:
        return index;
      case MULTI:
      case NO_PARENT:
        return Object.values(index);
    }
    // TODO handle case when it's undefined. We should always return an array.
  }
  getListLikeChildren() {
    return this.getChildren().filter((el) => CONTAINER_TYPES.includes(el.type));
  }
  getTaskLikeChildren() {
    return this.getChildren().filter((el) => !CONTAINER_TYPES.includes(el.type));
  }
  getGroupedChildren() {
    return [...this.getListLikeChildren(), ...this.getTaskLikeChildren()];
  }
  getParent(relation) {
    if (!this[relation]) return undefined;
    return this.cache.get(this[relation].key);
  }
  getPosition(relation) {
    if (!this[relation]) {
      return undefined;
    }
    const parent = this.cache.items[this[relation]?.key];
    return parent?.getChildren().indexOf(this);
  }
  getText() {
    return this.type === "date" ? formatDateItem(this) : this.name;
  }
  getSecondaryText() {
    return this.type === "date" ? formatDateItem(this, false) : this.name;
  }
  getShortText() {
    if (this.name.length > 40) {
      return this.name.slice(0, 33) + "...";
    }
    return this.name;
  }
  getCategory() {
    return ORDINARY_TYPES.includes(this.type)
      ? "task-like"
      : this.childRel === REL_PLAN
      ? "date-like"
      : "list-like";
  }
  getIcon() {
    const isDone = !!this.doneAtTimestampMs;
    if (this.icon) {
      if (this.type === "task") {
        switch (this.icon) {
          case "favorite":
            return isDone ? "favorite" : "favorite_outline";
          case "flag":
            return isDone ? "flag" : "outlined_flag";
          case "help":
            return isDone ? "help" : "help_outline";
        }
      }
      return this.icon;
    }
    if (this.type === "task") {
      return isDone ? "check_box" : "check_box_outline_blank";
    }
    const icon = typeIconMapping[this.type];
    if (!icon) {
      console.error("No icon defined for type", this.type);
      return "";
    }
    return icon;
  }
  isActionable() {
    return ["task", "goal"].includes(this.type);
  }
  isDeletable() {
    return !this.readOnly && !this.undeletable;
  }
  static generateId(prefix) {
    const timeString = Date.now().toString(36);
    const randomString = Math.random().toString(36).substring(2, 11);
    return `${prefix}:${timeString}.${randomString}`;
  }
  createChild(type, promptText = "Item:") {
    let name = prompt(promptText, "");
    const childRel = getRelationForType(type);
    if (name) {
      const doc = {
        _id: Item.generateId(prefixMap[type]),
        name,
        type,
        childRel,
      };
      const newItem = new Item(doc, this.cache, this.db);
      appendItem(this, newItem).save();
    }
  }
  createItem(type, name) {
    const childRel = getRelationForType(type);
    if (name) {
      const doc = {
        _id: Item.generateId(prefixMap[type]),
        name,
        type,
        childRel,
      };
      const newItem = new Item(doc, this.cache, this.db);
      appendItem(this, newItem).save();
    }
  }
  // Objects in reactive cache should be read-only. To make an update,
  // we create a clone first and update it.
  clone(update = {}) {
    const clone = new Item(this, this.cache, this.db);
    if (update.delKey) {
      delete clone[update.delKey];
      delete update.delKey;
    }
    Object.assign(clone, update);
    return clone;
  }
  save() {
    // This is not as circular as it looks, as .db is not an enumerable
    // property and won't be included in put request.
    const item = migrationEnabled ? migrateToOldFormat(this, this.db) : this;
    return this.db.put(item);
  }
  static saveAll(items) {
    return Promise.all(items.map((item) => item.save()));
  }
  trash() {
    if (!this.isDeletable) {
      alert(`This item cannot be deleted.`);
      return;
    }

    function countChildren(item) {
      let count = 1;
      if (item.childRel === REL_ELEM) {
        for (const elem of item.getChildren()) {
          count += countChildren(elem);
        }
      }
      return count;
    }
    const childCount = countChildren(this) - 1;
    if (childCount) {
      const confirm = window.confirm(
        `Really delete "${this.getShortText()}" and ${childCount} elements inside?`,
      );
      if (!confirm) {
        return;
      }
    }

    const itemsToUpdate = [];

    function markForDeletion(item) {
      itemsToUpdate.push(item.clone({ trashed: true }));
      if (item.childRel === REL_ELEM) {
        for (const elem of item.getChildren()) {
          markForDeletion(elem);
        }
      }
    }
    markForDeletion(this);

    for (const item of itemsToUpdate) {
      selection.remove(item);
    }
    router.removeFromView(this._id);
    return Item.saveAll(itemsToUpdate);
  }
  open(event) {
    router.openItem(this, event);
  }
  edit() {
    let name = prompt("Item text:", this.name);
    if (name) {
      this.clone({ name }).save();
    }
  }
  archive() {
    this.clone({ archivedAtTimestampMs: nowTimestamp() }).save();
  }
  duplicate() {
    duplicateItem(this.cache, this);
  }
  toggleDone() {
    if (this.type !== "task") {
      return;
    }
    if (!!this.doneAtTimestampMs) {
      this.clone({ delKey: "doneAtTimestampMs" }).save();
    } else {
      const copy = this.clone({ doneAtTimestampMs: nowTimestamp() });
      if (copy.repeat) {
        handleRepeatingItem(copy);
      }
      copy.save();
      if (!hintsShown.doneVsSelect) {
        window.alert("Task marked as done!\nIf you're trying to select, use ctrl+click.")
        hintsShown.doneVsSelect = true
      }
    }
  }
  toggleFolded() {
    const folded = !this.localFolded;
    return this.updateLocalData({ folded });
  }
  schedule(dateID) {
    const dateItem = this.cache.get(dateID);
    return appendItems(dateItem, [this])[0].save();
  }
  unschedule() {
    return clearParents([this], REL_PLAN)[0].save();
  }
  clearLabels() {
    return clearParents([this], REL_LABL)[0].save();
  }
  isVisible() {
    return (
      (!this.doneAtTimestampMs || !settings.hideDone) &&
      (!this.archivedAtTimestampMs || !settings.hideArchived)
    );
  }
  isShared() {
    return this.db.name !== this.cache.defaultDB.name;
  }
  copyIDtoClipboard() {
    navigator.clipboard.writeText(this._id);
  }
  toggleAllElems() {
    for (const el of this.getChildren()) {
      selection.toggle(el);
    }
  }
  setHome() {
    throw "not implemented";
  }
  downloadAsText() {
    downloadPlaintext(this.asText(), this.name);
  }
  asText(indent = -1) {
    let result = this.name + "\n";
    if (indent >= 0) {
      result = "    ".repeat(indent) + "  - " + result;
    }
    for (const el of this.getChildren()) {
      result += el.asText(indent + 1);
    }
    return result;
  }
}

Object.defineProperties(Item.prototype, {
  localData: {
    get() {
      return this.cache.local[this._id];
    },
  },
  localDataId: {
    get() {
      return "_local/" + this._id;
    },
  },
  updateLocalData: {
    // In-memory update is immediate, but the persistent storage update
    // is async.
    async value(updatesDict, optDeleteList) {
      const current = this.localData || { _id: this.localDataId };
      const modified = Object.assign({}, current, updatesDict);
      if (optDeleteList && optDeleteList.length) {
        for (const attr of optDeleteList) delete modified[attr];
      }
      Vue.set(this.cache.local, this._id, modified);
      // TODO: Handle 409? For now let's assume that the turnover is fast
      // enough to avoid race conditions.
      const response = await this.db.put(modified);
      this.localData._rev = response.rev;
      return response;
    },
  },
  // Getter for the state of being folded.
  //
  // This should really be called just `folded`, but it clashes with the name
  // already present on some items and causes error when calling Object.assign
  // (as then it tries to assign to a read only property).
  //
  // TODO: Rename back to `folded` once we make sure the `folded` property
  // is permanently purged from the database or we figure out a better way
  // to handle this kind of obsolete fields.
  localFolded: {
    get() {
      if (!this.localData || this.localData.folded === undefined) {
        return settings.defaultFolded;
      } else {
        return Boolean(this.localData && this.localData.folded);
      }
    },
  },
  preferredView: {
    get() {
      return this.localData?.preferredView;
    },
  },
});

function handleRepeatingItem(item) {
  if (item.repeat.repeated) {
    alert("Next occurence already scheduled.");
  } else {
    if (repeatingDoesntMakeSense(item)) {
      alert("This repetition setting does not make sense.");
      return;
    }
    const destination = {
      key: calcRepeatDate(item),
      seq: item[REL_PLAN].seq,
    };
    duplicateItem(item.cache, item, null, destination);
    item.repeat.repeated = true;
    alert(
      "Next occurrence scheduled in" + ` ${item.repeat.increment} ${item.repeat.unit}`,
    );
  }
}

function duplicateItem(reactiveCache, item, elemDest = null, planDest = null) {
  const clearedAttributes = {
    _id: Item.generateId(prefixMap[item.type]),
    _rev: undefined,
    doneAtTimestampMs: undefined,
  };
  const newItem = item.clone(clearedAttributes);
  if (newItem.repeat) {
    // copy original values, but create a new object.
    // Otherwise closing the original item will affect new instance.
    newItem.repeat = {
      increment: newItem.repeat.increment,
      unit: newItem.repeat.unit,
    };
  }
  if (elemDest) {
    newItem[REL_ELEM] = elemDest;
  }
  if (planDest) {
    newItem[REL_PLAN] = planDest;
  }
  newItem.save();

  for (const element of item.getChildren()) {
    const destination = {
      key: newItem._id,
      seq: element[REL_ELEM].seq,
    };
    duplicateItem(reactiveCache, element, destination);
  }
}
